import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useForm, useWatch } from "react-hook-form";

import type { DicoProjectConfigurationDto, UserDto } from "@/api/types";
import { Breadcrumbs } from "@/components/Breadcrumbs/Breadcrumbs";
import { Divider } from "@/components/Divider/Divider";
import { ErrorPage } from "@/components/Error/ErrorPage";
import { Form } from "@/components/Form/Form";
import { FormCheckbox } from "@/components/Form/FormCheckbox";
import { FormInput } from "@/components/Form/FormInput";
import { FormSelect } from "@/components/Form/FormSelect";
import { DocumentPage } from "@/components/Page/DocumentPage";
import { Button } from "@/design-system/Button";
import { FormField } from "@/design-system/FormField";
import { FullSizeLoader } from "@/design-system/FullSizeLoader";
import { SearchableSelect } from "@/design-system/SearchableSelect";
import { createRequiredStringRule } from "@/helpers/rules";
import { usePermission } from "@/hooks/usePermission";
import { useSlug } from "@/hooks/useSlug";
import { integrationMutations } from "@/queries/integrations/mutations";
import { useIntegrationQueries } from "@/queries/integrations/queryOptions";
import { useTicketCategoryQueries } from "@/queries/ticket-categories/queryOptions";
import { useUserQueries } from "@/queries/users/queryOptions";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";

type DicoPreset = Pick<
  DicoProjectConfigurationDto,
  "url" | "username" | "buyerGln" | "buyerName" | "contractorGln" | "contractorName" | "appendTicketUrlToInstructions"
> & {
  name: string;
};

const BASE_DICO_PRESET: Omit<DicoPreset, "name" | "contractorGln"> = {
  url: "https://production.pcamobile.com/outbounddispatch/restservice/fe254147-3e5d-430b-8474-d7e0bd9daccc/message/MaintenanceInstruction",
  username: "AreaOfPeopleBO",
  buyerGln: "873784972432",
  buyerName: "MVGM Wonen",
  contractorName: "Jansen Huybregts",
  appendTicketUrlToInstructions: true,
} as const;

const DICO_PRESETS: DicoPreset[] = [
  { ...BASE_DICO_PRESET, name: "MVGM - Jansen Huybregts (8713783644775)", contractorGln: "8713783644775" },
  { ...BASE_DICO_PRESET, name: "MVGM - Jansen Huybregts (8719332648072)", contractorGln: "8719332648072" },
  { ...BASE_DICO_PRESET, name: "MVGM - Jansen Huybregts (8719332119695)", contractorGln: "8719332119695" },
  { ...BASE_DICO_PRESET, name: "MVGM - Jansen Huybregts (8713783664087)", contractorGln: "8713783664087" },
  { ...BASE_DICO_PRESET, name: "MVGM - Jansen Huybregts (8713783667552)", contractorGln: "8713783667552" },
  { ...BASE_DICO_PRESET, name: "MVGM - Jansen Huybregts (8713783662922)", contractorGln: "8713783662922" },
  { ...BASE_DICO_PRESET, name: "MVGM - Jansen Huybregts (8713783661666)", contractorGln: "8713783661666" },
  { ...BASE_DICO_PRESET, name: "MVGM - Jansen Huybregts (8719332105155)", contractorGln: "8719332105155" },
  { ...BASE_DICO_PRESET, name: "MVGM - Jansen Huybregts (8719332649789)", contractorGln: "8719332649789" },
  { ...BASE_DICO_PRESET, name: "MVGM - Jansen Huybregts (8713783672556)", contractorGln: "8713783672556" },
  { ...BASE_DICO_PRESET, name: "MVGM - Jansen Huybregts (8713783668337)", contractorGln: "8713783668337" },
  { ...BASE_DICO_PRESET, name: "MVGM - Jansen Huybregts (8713783662342)", contractorGln: "8713783662342" },
];

export type DicoFormValues = Omit<DicoProjectConfigurationDto, "fallbackUserId" | "assigneeUserId"> & {
  fallbackUser: UserDto | undefined;
  assigneeUser: UserDto | undefined;
};

export function DicoPage(): React.ReactNode {
  const { t } = useTranslation();
  const slug = useSlug();
  const hasPermission = usePermission();
  const integrationQueries = useIntegrationQueries();
  const userQueries = useUserQueries();
  const ticketCategoryQueries = useTicketCategoryQueries();

  const {
    data: config,
    isFetching: isLoadingConfig,
    error: configError,
  } = useQuery(integrationQueries.dicoConfiguration());
  const {
    data: adminUsers,
    isFetching: isLoadingAdminUsers,
    error: adminUsersError,
  } = useQuery(userQueries.listAdmins());
  const {
    data: assignees,
    isFetching: isLoadingAssignees,
    error: assigneesError,
  } = useQuery(ticketCategoryQueries.listAssignees());
  const mutation = integrationMutations.useEditDicoConfiguration();

  if (!hasPermission((x) => x.integrations.canManageDico)) {
    return <ErrorPage status={404} />;
  }

  if (configError || adminUsersError || assigneesError) {
    return <ErrorPage error={configError || adminUsersError || assigneesError} />;
  }

  if (isLoadingConfig || isLoadingAdminUsers || isLoadingAssignees) {
    return <FullSizeLoader withPadding />;
  }

  const name = t("page.integration.dico.title");

  return (
    <DocumentPage
      containerBoundary="inherit"
      contentSize="constrained"
      tabTitle={name}
      breadcrumbs={
        <Breadcrumbs
          pages={[{ name: t("page.integration.title"), to: routes.projects.integrations({ slug }) }, { name }]}
        />
      }
    >
      <DicoForm
        defaultValues={{
          enabled: config?.enabled || false,
          url: config?.url || "",
          username: config?.username || "",
          password: config?.password || "",
          buyerGln: config?.buyerGln || "",
          buyerName: config?.buyerName || "",
          contractorGln: config?.contractorGln || "",
          contractorName: config?.contractorName || "",
          fallbackUser:
            !config?.fallbackUserId || !adminUsers ? undefined : adminUsers.find((u) => u.id === config.fallbackUserId),
          assigneeUser:
            !config?.assigneeUserId || !adminUsers ? undefined : adminUsers.find((u) => u.id === config.assigneeUserId),
          ignoreCategories: config?.ignoreCategories || false,
        }}
        assignees={assignees || []}
        isSubmitting={mutation.isPending}
        onSubmit={mutation.mutate}
      />
    </DocumentPage>
  );
}

interface DicoFormProps {
  assignees: UserDto[] | undefined;
  defaultValues: Partial<DicoFormValues>;
  isSubmitting: boolean;
  onSubmit: (data: DicoProjectConfigurationDto) => void;
}

function DicoForm({ assignees, defaultValues, isSubmitting, onSubmit }: DicoFormProps): React.ReactNode {
  const [selectedPreset, setSelectedPreset] = useState<DicoPreset | undefined>(undefined);
  const { t } = useTranslation();
  const formMethods = useForm<DicoFormValues>({ defaultValues });

  const enabled = useWatch({ control: formMethods.control, name: "enabled" });

  function handlePresetChange(preset: DicoPreset | undefined) {
    setSelectedPreset(preset);

    if (preset) {
      formMethods.setValue("url", preset.url);
      formMethods.setValue("username", preset.username);
      formMethods.setValue("buyerGln", preset.buyerGln);
      formMethods.setValue("buyerName", preset.buyerName);
      formMethods.setValue("contractorGln", preset.contractorGln);
      formMethods.setValue("contractorName", preset.contractorName);
      formMethods.setValue("appendTicketUrlToInstructions", preset.appendTicketUrlToInstructions);
      formMethods.setValue("password", "");
    }
  }

  function handleSubmit(formValues: DicoFormValues) {
    onSubmit({
      ...formValues,
      fallbackUserId: formValues.fallbackUser?.id ?? "",
      assigneeUserId: formValues.assigneeUser?.id,
    });
  }

  function handleToggleEnabled(newState: boolean) {
    if (!newState) {
      formMethods.clearErrors();
    }
  }

  function handleChangeContractorGln(value: string) {
    const matchingPreset = DICO_PRESETS.find((preset) => preset.contractorGln === value);

    if (matchingPreset) {
      setSelectedPreset(matchingPreset);
    } else {
      setSelectedPreset(undefined);
    }
  }

  return (
    <Form.Root formMethods={formMethods} onSubmit={handleSubmit}>
      <Form.Container title={t("page.integration.dico.title")}>
        <Form.Body>
          <FormCheckbox<DicoFormValues, "enabled">
            id="enabled"
            name="enabled"
            label={t("common.action.enable")}
            onChange={(e) => {
              handleToggleEnabled(e.target.checked);
            }}
          />
          <FormField label={t("page.integration.dico.form.preset")}>
            <div className="max-w-search-wide">
              <SearchableSelect
                emptyItem={t("page.integration.dico.form.preset.none")}
                searchableFieldSelector={(item) => [item.name]}
                placeholder={t("page.integration.dico.form.preset.placeholder")}
                items={DICO_PRESETS}
                keySelector={(preset) => preset.contractorGln}
                renderOption={(preset) => preset.name}
                selected={selectedPreset}
                disabled={!enabled}
                onChange={handlePresetChange}
              />
            </div>
          </FormField>
          <Divider />

          <FormField label={t("page.integration.dico.form.url")} required>
            <FormInput<DicoFormValues, "url">
              id="url"
              name="url"
              rules={{
                validate: {
                  required: enabled ? createRequiredStringRule(t, "page.integration.dico.form.url") : () => undefined,
                },
              }}
              disabled={!enabled}
            />
          </FormField>

          <FormField label={t("page.integration.dico.form.username")} required>
            <FormInput<DicoFormValues, "username">
              id="username"
              name="username"
              rules={{
                validate: {
                  required: enabled
                    ? createRequiredStringRule(t, "page.integration.dico.form.username")
                    : () => undefined,
                },
              }}
              disabled={!enabled}
            />
          </FormField>

          <FormField label={t("page.integration.dico.form.password")} required>
            <FormInput<DicoFormValues, "password">
              id="password"
              name="password"
              type="password"
              rules={{
                validate: {
                  required: enabled
                    ? createRequiredStringRule(t, "page.integration.dico.form.password")
                    : () => undefined,
                },
              }}
              disabled={!enabled}
            />
          </FormField>

          <FormField
            label={t("page.integration.dico.form.buyer-gln")}
            required
            tooltip={t("page.integration.dico.form.buyer-gln.tooltip")}
          >
            <FormInput<DicoFormValues, "buyerGln">
              name="buyerGln"
              rules={{
                validate: {
                  required: enabled
                    ? createRequiredStringRule(t, "page.integration.dico.form.buyer-gln")
                    : () => undefined,
                  mustOnlyContainNumbers: (value) => {
                    if (!enabled || !value) {
                      return undefined;
                    }

                    return /^\d+$/.test(value) ? undefined : t("components.form.error.must-only-contain-numbers");
                  },
                },
              }}
              placeholder="0000000000000"
              disabled={!enabled}
            />
          </FormField>

          <FormField label={t("page.integration.dico.form.buyer-name")} required>
            <FormInput<DicoFormValues, "buyerName">
              name="buyerName"
              rules={{
                validate: {
                  required: enabled
                    ? createRequiredStringRule(t, "page.integration.dico.form.buyer-name")
                    : () => undefined,
                },
                maxLength: {
                  message: t("components.form.error.max-length", { length: 100 }),
                  value: 100,
                },
              }}
              disabled={!enabled}
            />
          </FormField>

          <FormField
            label={t("page.integration.dico.form.contractor-gln")}
            required
            tooltip={t("page.integration.dico.form.contractor-gln.tooltip")}
          >
            <FormInput<DicoFormValues, "contractorGln">
              id="contractorGln"
              name="contractorGln"
              rules={{
                validate: {
                  required: enabled
                    ? createRequiredStringRule(t, "page.integration.dico.form.contractor-gln")
                    : () => undefined,
                  mustOnlyContainNumbers: (value) => {
                    if (!enabled || !value) {
                      return undefined;
                    }

                    return /^\d+$/.test(value) ? undefined : t("components.form.error.must-only-contain-numbers");
                  },
                },
              }}
              onChange={(e) => handleChangeContractorGln(e.target.value)}
              placeholder="0000000000000"
              disabled={!enabled}
            />
          </FormField>

          <FormField label={t("page.integration.dico.form.contractor-name")} required>
            <FormInput<DicoFormValues, "contractorName">
              id="contractorName"
              name="contractorName"
              rules={{
                validate: {
                  required: enabled
                    ? createRequiredStringRule(t, "page.integration.dico.form.contractor-name")
                    : () => undefined,
                },
                maxLength: {
                  message: t("components.form.error.max-length", { length: 100 }),
                  value: 100,
                },
              }}
              disabled={!enabled}
            />
          </FormField>

          <FormCheckbox<DicoFormValues, "ignoreCategories">
            id="ignoreCategories"
            name="ignoreCategories"
            label={t("page.integration.dico.form.ignore-categories")}
            tooltip={t("page.integration.dico.form.ignore-categories.tooltip")}
          />

          <FormField
            label={t("page.integration.dico.form.fallback-user")}
            required
            tooltip={t("page.integration.dico.form.fallback-user.tooltip")}
          >
            <div className="max-w-search-wide">
              <FormSelect<DicoFormValues, UserDto>
                name="fallbackUser"
                items={assignees || []}
                keySelector={(user) => user.id}
                renderOption={(user) => `${user.firstName} ${user.lastName}`}
                rules={{
                  validate: {
                    required: (value, formValues) => {
                      if (formValues.enabled && !value) {
                        return t("components.form.error.required", {
                          inputName: t("page.integration.dico.form.fallback-user"),
                        });
                      }
                    },
                  },
                }}
                disabled={isSubmitting || !enabled}
              />
            </div>
          </FormField>

          <FormField
            label={t("page.integration.dico.form.assignee-user")}
            tooltip={t("page.integration.dico.form.assignee-user.tooltip")}
          >
            <div className="max-w-search-wide">
              <FormSelect<DicoFormValues, UserDto>
                name="assigneeUser"
                items={assignees || []}
                keySelector={(user) => user.id}
                renderOption={(user) => `${user.firstName} ${user.lastName}`}
                disabled={isSubmitting || !enabled}
              />
            </div>
          </FormField>
        </Form.Body>
        <Form.Footer
          renderPrimaryAction={() => (
            <Button type="submit" isLoading={isSubmitting}>
              {t("common.action.save")}
            </Button>
          )}
        />
      </Form.Container>
    </Form.Root>
  );
}
